import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {current} from 'immer';
import type {
  Row,
  RowType,
  WidgetConfig,
  DashboardStatus,
  WidgetType,
} from 'src/types/byod';
import {DASHBOARD_STATUS, ROW_TYPE, WIDGET_TYPE} from 'src/types/byod';
import {
  MAX_WIDGETS,
  MAX_TILES_PER_ROW,
} from 'src/components/analytics-genesis/byod/constants';
import {mintId} from 'src/utils/id';
import logger from 'src/utils/logger';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function countNonSectionWidgets(
  widgets: Record<string, WidgetConfig>,
): number {
  return Object.values(widgets).filter(
    (w) => w.widget_type !== WIDGET_TYPE.SECTION,
  ).length;
}

function rowTypeFromWidgetType(widgetType: WidgetType | undefined): RowType {
  if (widgetType === WIDGET_TYPE.KPI) {
    return ROW_TYPE.KPI;
  }
  if (widgetType === WIDGET_TYPE.SECTION) {
    return ROW_TYPE.SECTION;
  }
  return ROW_TYPE.VIZ;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export const SAVE_STATE = {
  IDLE: 'idle',
  DIRTY: 'dirty',
  SAVING: 'saving',
  ERROR: 'error',
} as const;
export type SaveState = typeof SAVE_STATE[keyof typeof SAVE_STATE];

/**
 * Snapshot captured when the owner clicks "Edit" on a published dashboard.
 * Edits accumulate in the live store; Cancel restores from the snapshot,
 * Save & Publish clears it. A null snapshot means we're not in an active
 * edit session (i.e. viewing published, or creating from scratch).
 */
export type EditSnapshot = {
  name: string;
  description: string;
  rowOrder: string[];
  rows: Record<string, Row>;
  widgets: Record<string, WidgetConfig>;
};

interface DashboardState {
  id: string | null;
  name: string;
  description: string;
  status: DashboardStatus;
  createdById: string | null;
  rowOrder: string[];
  rows: Record<string, Row>;
  widgets: Record<string, WidgetConfig>;
  saveState: SaveState;
  saveError: Error | null;
  /**
   * Per-attempt token set by `markSaving(nonce)` and cleared on every
   * terminal save transition (`markSaved`/`markError`/`reset`/`cancelEdit`/
   * `applySaveAndPublishResponse`/`initDashboard`). Callers compare the
   * captured nonce against `getState().saveNonce` after `await mutate()`
   * to detect "the dashboard underneath us changed" — including the
   * `id===null` create path where comparing dashboard ids alone is
   * insufficient (a fresh draft also has `id===null`).
   */
  saveNonce: string | null;
  /** Present only while editing a published dashboard. Restored on Cancel. */
  editSnapshot: EditSnapshot | null;
  /**
   * Tile id the canvas should scroll into view on next paint. Set when a
   * panel (preview / edit) closes so the user lands back on the widget they
   * were working with. Cleared by the canvas once it scrolls.
   */
  focusTileId: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type InitDashboardParams = {
  /** `null` for brand-new dashboards (no server ID until Save & Publish). */
  id: string | null;
  name: string;
  description?: string;
  createdById: string | null;
  rowOrder: string[];
  rows: Record<string, Row>;
  widgets: Record<string, WidgetConfig>;
  status?: DashboardStatus;
};

interface DashboardActions {
  // Canvas mutations — each auto-sets saveState=SAVE_STATE.DIRTY
  addWidget: (
    widget: WidgetConfig,
    position: {rowIndex?: number; colIndex?: number; rowId?: string},
  ) => boolean;
  updateWidget: (id: string, patch: Partial<WidgetConfig>) => void;
  removeWidget: (id: string) => void;
  addSectionRow: (afterIndex: number, label?: string) => void;
  updateRowLabel: (rowId: string, label: string) => void;
  removeSectionRow: (rowId: string) => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;

  /**
   * Reorder rows within the canvas. Moves the row at `fromIndex` to
   * `toIndex` in `rowOrder`. Marks the store dirty in saveable modes so the
   * change lands in Save & Publish.
   */
  reorderRows: (fromIndex: number, toIndex: number) => void;

  /**
   * Move a tile across rows of the same type, or reorder within a single
   * row. Both indices are into the `tiles` arrays of their respective rows.
   * If the move would exceed the target row's tile cap, the call is a no-op
   * (frontend-level guard; the drop indicator is permissive and the store
   * is the final authority).
   */
  moveTile: (
    fromRowId: string,
    toRowId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;

  /**
   * Move a tile out of its current row into a brand-new row inserted after
   * `afterRowIndex` (pass -1 to prepend as the first row). Used when the
   * user drops a tile on an inter-row drop zone in the canvas. Prunes the
   * source row if it becomes empty.
   */
  moveTileToNewRow: (
    fromRowId: string,
    fromIndex: number,
    afterRowIndex: number,
    newRowType: typeof ROW_TYPE.KPI | typeof ROW_TYPE.VIZ,
  ) => void;

  // Save lifecycle (called by usePublish hook, not by components)
  markDirty: () => void;
  /**
   * Optional `nonce` correlates an in-flight save with the dashboard
   * underneath it. Captured by callers, then compared against
   * `getState().saveNonce` post-await to detect navigation / reset
   * mid-flight (the create-path race where both old + new drafts have
   * `id === null`).
   */
  markSaving: (nonce?: string) => void;
  markSaved: () => void;
  markError: (err: Error) => void;

  // Identity
  setId: (id: string) => void;
  setStatus: (status: DashboardStatus) => void;

  /**
   * Queue a tile to scroll into view on the next canvas paint. Use when a
   * panel (preview / edit) closes so the user lands back on the widget they
   * just added or edited. Pass null to clear.
   */
  setFocusTile: (tileId: string | null) => void;

  // Edit session — snapshot for Cancel, commit on Save & Publish
  enterEditMode: () => void;
  cancelEdit: () => void;
  commitEdit: () => void;

  /**
   * Apply the response of a successful Save & Publish. Sets the dashboard
   * id (for the create path where the server mints it), flips status to
   * PUBLISHED, clears the edit snapshot, and resets saveState to IDLE.
   *
   * Widget IDs are NOT swapped — every placement id is a frontend-minted
   * UUID that the backend stored verbatim, so React keys and React Query
   * cache entries stay stable across the save (no remount, no skeleton
   * flash, no cache transfer needed).
   */
  applySaveAndPublishResponse: (response: {id: string}) => void;

  // Bulk init / reset
  initDashboard: (params: InitDashboardParams) => void;
  reset: () => void;

  // Derived — serializes rowOrder + rows back to Row[] for API calls
  getLayout: () => Row[];
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: DashboardState = {
  id: null,
  name: 'Untitled Dashboard',
  description: '',
  status: DASHBOARD_STATUS.DRAFT,
  createdById: null,
  rowOrder: [],
  rows: {},
  widgets: {},
  saveState: SAVE_STATE.IDLE,
  saveError: null,
  saveNonce: null,
  editSnapshot: null,
  focusTileId: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useDashboardStore = create<DashboardState & DashboardActions>()(
  immer((set, get) => ({
    ...initialState,

    // ── Canvas mutations ───────────────────────────────────────────────────

    addWidget: (widget, position) => {
      let added = false;
      set((state) => {
        // Enforce 20-widget limit (sections excluded)
        if (
          widget.widget_type !== WIDGET_TYPE.SECTION &&
          countNonSectionWidgets(state.widgets) >= MAX_WIDGETS
        ) {
          logger.warn('addWidget: max widget limit reached', {
            widgetId: widget.widget_id,
            current: countNonSectionWidgets(state.widgets),
            max: MAX_WIDGETS,
          });
          return;
        }

        if (position.rowId) {
          // Insert into existing row at colIndex
          const row = state.rows[position.rowId];
          if (!row) {
            logger.warn('addWidget: target row not found', {
              rowId: position.rowId,
              widgetId: widget.widget_id,
            });
            return;
          }
          state.widgets[widget.widget_id] = widget;
          const colIndex = position.colIndex ?? row.tiles.length;
          row.tiles.splice(colIndex, 0, widget.widget_id);
        } else {
          state.widgets[widget.widget_id] = widget;
          // Create a new row
          const newRowId = mintId();
          const newRow: Row = {
            id: newRowId,
            type: rowTypeFromWidgetType(widget.widget_type),
            tiles: [widget.widget_id],
          };
          state.rows[newRowId] = newRow;

          if (position.rowIndex === undefined) {
            // No position specified (e.g. header "Add Report") → append at end
            state.rowOrder.push(newRowId);
          } else if (position.rowIndex < 0) {
            // Explicit negative index (divider before first row) → prepend
            state.rowOrder.unshift(newRowId);
          } else {
            state.rowOrder.splice(position.rowIndex + 1, 0, newRowId);
          }
        }

        state.saveState = SAVE_STATE.DIRTY;
        added = true;
      });
      return added;
    },

    // `updateWidget` is called from two different contexts:
    //   - Saveable mode (create / edit) → change is persistent, mark dirty.
    //   - View mode on a published dashboard → change is ephemeral; the user
    //     may tweak chart style, column selection, or pagination/sort for
    //     their current session, but nothing is marked dirty and nothing is
    //     sent to the backend. The change is lost on navigation (re-hydrated
    //     from the server next time the dashboard loads).
    // "Saveable" = either creating (id null) or in an active edit session.
    updateWidget: (id, patch) =>
      set((state) => {
        if (!state.widgets[id]) {
          return;
        }
        Object.assign(state.widgets[id], patch);
        const isSaveable = state.id === null || state.editSnapshot !== null;
        if (isSaveable) {
          state.saveState = SAVE_STATE.DIRTY;
        }
      }),

    removeWidget: (id) =>
      set((state) => {
        delete state.widgets[id];
        // Remove widget from its row's tiles
        for (const rowId of state.rowOrder) {
          const row = state.rows[rowId];
          if (row) {
            row.tiles = row.tiles.filter((t) => t !== id);
          }
        }
        // Prune empty non-section rows
        const emptyRowIds = state.rowOrder.filter((rowId) => {
          const row = state.rows[rowId];
          return row && row.tiles.length === 0 && row.type !== ROW_TYPE.SECTION;
        });
        for (const rowId of emptyRowIds) {
          delete state.rows[rowId];
        }
        state.rowOrder = state.rowOrder.filter(
          (rowId) => !emptyRowIds.includes(rowId),
        );
        const isSaveable = state.id === null || state.editSnapshot !== null;
        if (isSaveable) {
          state.saveState = SAVE_STATE.DIRTY;
        }
      }),

    addSectionRow: (afterIndex, label = '') =>
      set((state) => {
        const newRowId = mintId();
        const newRow: Row = {
          id: newRowId,
          type: ROW_TYPE.SECTION,
          tiles: [],
          label,
        };
        state.rows[newRowId] = newRow;
        if (afterIndex < 0) {
          state.rowOrder.unshift(newRowId);
        } else {
          state.rowOrder.splice(afterIndex + 1, 0, newRowId);
        }
        state.saveState = SAVE_STATE.DIRTY;
      }),

    updateRowLabel: (rowId, label) =>
      set((state) => {
        const row = state.rows[rowId];
        if (!row || row.label === label) {
          // No change → do not mark dirty. The section row's Escape and
          // blur-without-edit paths both call this with the original label;
          // marking dirty there would falsely trigger the discard / leave
          // prompts on a dashboard the user never actually edited.
          return;
        }
        row.label = label;
        state.saveState = SAVE_STATE.DIRTY;
      }),

    removeSectionRow: (rowId) =>
      set((state) => {
        delete state.rows[rowId];
        state.rowOrder = state.rowOrder.filter((id) => id !== rowId);
        state.saveState = SAVE_STATE.DIRTY;
      }),

    reorderRows: (fromIndex, toIndex) =>
      set((state) => {
        if (
          fromIndex === toIndex ||
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= state.rowOrder.length ||
          toIndex >= state.rowOrder.length
        ) {
          return;
        }
        const [moved] = state.rowOrder.splice(fromIndex, 1);
        state.rowOrder.splice(toIndex, 0, moved);
        state.saveState = SAVE_STATE.DIRTY;
      }),

    moveTile: (fromRowId, toRowId, fromIndex, toIndex) =>
      set((state) => {
        const fromRow = state.rows[fromRowId];
        const toRow = state.rows[toRowId];
        if (!fromRow || !toRow) {
          return;
        }
        // Cap check — skip when moving within the same row (count unchanged).
        if (fromRowId !== toRowId) {
          const max = MAX_TILES_PER_ROW[toRow.type] ?? Infinity;
          if (toRow.tiles.length >= max) {
            return;
          }
          // Type mismatch would bypass the library's `type` prop only if a
          // caller wired it wrong — defend at the store boundary too.
          if (fromRow.type !== toRow.type) {
            return;
          }
        }
        const [tileId] = fromRow.tiles.splice(fromIndex, 1);
        if (!tileId) {
          return;
        }
        toRow.tiles.splice(toIndex, 0, tileId);
        // Prune empty non-section source rows (same rule as removeWidget).
        if (
          fromRow.tiles.length === 0 &&
          fromRow.type !== ROW_TYPE.SECTION &&
          fromRowId !== toRowId
        ) {
          delete state.rows[fromRowId];
          state.rowOrder = state.rowOrder.filter((id) => id !== fromRowId);
        }
        state.saveState = SAVE_STATE.DIRTY;
      }),

    moveTileToNewRow: (fromRowId, fromIndex, afterRowIndex, newRowType) =>
      set((state) => {
        const fromRow = state.rows[fromRowId];
        if (!fromRow) {
          return;
        }
        const tileId = fromRow.tiles[fromIndex];
        if (!tileId) {
          return;
        }
        const widget = state.widgets[tileId];
        if (!widget) {
          return;
        }
        // Tile's widget_type must match the new row type. Sections never land
        // here (handled by section-row rendering), so only kpi/chart/table.
        const expectedRowType: 'kpi' | 'viz' =
          widget.widget_type === WIDGET_TYPE.KPI ? 'kpi' : 'viz';
        if (expectedRowType !== newRowType) {
          return;
        }
        // Remove tile from source.
        fromRow.tiles.splice(fromIndex, 1);
        // Create the new row.
        const newRowId = mintId();
        const rowType = newRowType === 'kpi' ? ROW_TYPE.KPI : ROW_TYPE.VIZ;
        state.rows[newRowId] = {id: newRowId, type: rowType, tiles: [tileId]};
        // Insert at afterRowIndex + 1; afterRowIndex === -1 prepends.
        const insertIndex = Math.min(
          state.rowOrder.length,
          Math.max(0, afterRowIndex + 1),
        );
        state.rowOrder.splice(insertIndex, 0, newRowId);
        // Prune empty non-section source row.
        if (fromRow.tiles.length === 0 && fromRow.type !== ROW_TYPE.SECTION) {
          delete state.rows[fromRowId];
          state.rowOrder = state.rowOrder.filter((id) => id !== fromRowId);
        }
        state.saveState = SAVE_STATE.DIRTY;
      }),

    setName: (name) =>
      set((state) => {
        state.name = name;
        state.saveState = SAVE_STATE.DIRTY;
      }),

    setDescription: (description) =>
      set((state) => {
        state.description = description;
        state.saveState = SAVE_STATE.DIRTY;
      }),

    // ── Save lifecycle ─────────────────────────────────────────────────────

    markDirty: () =>
      set((state) => {
        state.saveState = SAVE_STATE.DIRTY;
        state.saveError = null;
      }),

    markSaving: (nonce) =>
      set((state) => {
        state.saveState = SAVE_STATE.SAVING;
        state.saveNonce = nonce ?? null;
      }),

    markSaved: () =>
      set((state) => {
        // Only transition to idle if still saving — if a mutation dirtied
        // the store mid-flight, leave it as 'dirty' so the next save catches it.
        if (state.saveState === SAVE_STATE.SAVING) {
          state.saveState = SAVE_STATE.IDLE;
          state.saveError = null;
          state.saveNonce = null;
        }
      }),

    markError: (err) =>
      set((state) => {
        state.saveState = SAVE_STATE.ERROR;
        state.saveError = err;
        state.saveNonce = null;
      }),

    // ── Identity ───────────────────────────────────────────────────────────

    setId: (id) =>
      set((state) => {
        state.id = id;
      }),

    setStatus: (status) =>
      set((state) => {
        state.status = status;
      }),

    setFocusTile: (tileId) =>
      set((state) => {
        state.focusTileId = tileId;
      }),

    // ── Edit session ───────────────────────────────────────────────────────

    // Called when the owner clicks "Edit" on a published dashboard. Captures a
    // deep-cloned snapshot of the current editable state so Cancel can restore.
    // No-op if already in an edit session (prevents clobbering on double-click).
    enterEditMode: () =>
      set((state) => {
        if (state.editSnapshot !== null) {
          return;
        }
        state.editSnapshot = {
          name: state.name,
          description: state.description,
          rowOrder: [...state.rowOrder],
          rows: current(state.rows),
          widgets: current(state.widgets),
        };
      }),

    // Restores everything to the snapshot and clears it. Used by the Cancel
    // button after the user confirms discarding changes.
    // Refuses when a save is in flight — otherwise the snapshot restores
    // but the pending POST response would still land via
    // `applySaveAndPublishResponse` and silently re-publish the state the
    // user just discarded. Callers should disable Cancel while SAVING; this
    // is the defensive backstop.
    cancelEdit: () =>
      set((state) => {
        const snap = state.editSnapshot;
        if (!snap) {
          return;
        }
        if (state.saveState === SAVE_STATE.SAVING) {
          return;
        }
        state.name = snap.name;
        state.description = snap.description;
        state.rowOrder = [...snap.rowOrder];
        state.rows = snap.rows;
        state.widgets = snap.widgets;
        state.editSnapshot = null;
        state.saveState = SAVE_STATE.IDLE;
        state.saveError = null;
        state.saveNonce = null;
      }),

    // Called after a successful Save & Publish. The snapshot is no longer
    // needed because the current state is now the new baseline.
    commitEdit: () =>
      set((state) => {
        state.editSnapshot = null;
      }),

    applySaveAndPublishResponse: ({id}) =>
      set((state) => {
        // No widget id swap: every placement id is a frontend-minted UUID
        // and the backend stored it verbatim. The store's widget keys and
        // row.tiles entries already match what's in the DB. Just settle
        // dashboard-level metadata: claim the (possibly newly-minted)
        // dashboard id, flip to PUBLISHED, drop the edit snapshot.
        state.id = id;
        state.status = DASHBOARD_STATUS.PUBLISHED;
        state.editSnapshot = null;
        // Only clear save state if we're still SAVING. If the user edited
        // during the in-flight POST the reducer already flipped us to DIRTY;
        // force-resetting IDLE here silently loses those edits (no beforeunload,
        // no save prompt on leave) and the next save would skip them. Mirrors
        // the guard in `markSaved`.
        if (state.saveState === SAVE_STATE.SAVING) {
          state.saveState = SAVE_STATE.IDLE;
          state.saveError = null;
          state.saveNonce = null;
        }
      }),

    // ── Bulk init ──────────────────────────────────────────────────────────

    initDashboard: ({
      id,
      name,
      description,
      createdById,
      rowOrder,
      rows,
      widgets,
      status,
    }) =>
      set((state) => {
        state.id = id;
        state.name = name;
        state.description = description ?? '';
        state.createdById = createdById;
        state.rowOrder = rowOrder;
        state.rows = rows;
        state.widgets = widgets;
        state.status = status ?? DASHBOARD_STATUS.DRAFT;
        state.saveState = SAVE_STATE.IDLE;
        state.saveError = null;
        state.saveNonce = null;
        // Reset edit-session artifacts: otherwise navigating from an in-edit
        // dashboard into a fresh one would leave `editSnapshot`/`focusTileId`
        // pointing at the previous dashboard, corrupting canEdit gating and
        // the next Save & Publish diff.
        state.editSnapshot = null;
        state.focusTileId = null;
      }),

    reset: () => set(() => ({...initialState})),

    getLayout: (): Row[] => {
      const state = get();
      return state.rowOrder.map((id) => state.rows[id]).filter(Boolean);
    },
  })),
);
